import { useEffect, useRef, useState } from "react"

import { useDebouncedCallback } from "../../../../hooks/use-debounced-callback.ts"
import {
  getCustomerNameMatches,
  searchCustomers
} from "../../../CustomersPage/api/services.ts"
import type { CustomerSearchResult } from "../../../CustomersPage/types/customer.types.ts"
import {
  type CustomerMatchCandidate,
  MIN_MATCHABLE_PHONE_DIGITS,
  NAME_MATCH_PAGE_SIZE,
  fromNameMatch,
  fromSearchResult,
  isEmailMatch,
  isPhoneMatch,
  normalizeEmailForMatch,
  phoneDigits,
  rankCustomerMatches
} from "./customer-match.ts"

const MATCH_DEBOUNCE_MS = 500
const MATCH_PAGE_SIZE = 20

interface CustomerMatchInput {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  phone?: string | null
}

function searchResultsOf(data: unknown): CustomerSearchResult[] {
  if (Array.isArray(data)) return data as CustomerSearchResult[]
  const results = (data as { results?: CustomerSearchResult[] })?.results
  return Array.isArray(results) ? results : []
}

async function searchExact(query: string): Promise<CustomerSearchResult[]> {
  const response = await searchCustomers({
    q: query,
    page: 1,
    page_size: MATCH_PAGE_SIZE
  })
  return response.ok ? searchResultsOf(response.data) : []
}

/**
 * The duplicate prompt behind step 1. Warn-only: a failed lookup shows nothing
 * rather than blocking, because a candidate list is advice and its absence must
 * never stop a lead being created.
 */
function useCustomerMatches(typed: CustomerMatchInput) {
  const [matches, setMatches] = useState<CustomerMatchCandidate[]>([])
  const [loading, setLoading] = useState(false)
  // Options are acted on by index into this list, so a response landing out of
  // order would repoint the row the user is about to click.
  const latest = useRef(0)

  const lookup = useDebouncedCallback((input: CustomerMatchInput) => {
    void (async () => {
      const first = (input.first_name ?? "").trim()
      const last = (input.last_name ?? "").trim()
      const email = (input.email ?? "").trim()
      const phone = (input.phone ?? "").trim()

      const wantsName = !!(first || last)
      const wantsEmail = normalizeEmailForMatch(email).includes("@")
      const wantsPhone = phoneDigits(phone).length >= MIN_MATCHABLE_PHONE_DIGITS

      const seq = ++latest.current

      if (!wantsName && !wantsEmail && !wantsPhone) {
        setMatches([])
        setLoading(false)
        return
      }

      setLoading(true)

      try {
        const [named, byEmail, byPhone] = await Promise.all([
          wantsName
            ? getCustomerNameMatches({
                first_name: first,
                last_name: last,
                limit: NAME_MATCH_PAGE_SIZE
              })
            : null,
          wantsEmail ? searchExact(email) : null,
          wantsPhone ? searchExact(phone) : null
        ])

        if (seq !== latest.current) return

        const handles = { email, phone }
        const candidates: CustomerMatchCandidate[] = [
          ...(named?.ok ? (named.data ?? []) : []).map((match) =>
            fromNameMatch(match, handles)
          ),
          // The autocomplete is a substring search; only an exact handle is a
          // duplicate signal, so a partial hit is dropped rather than shown.
          ...(byEmail ?? [])
            .filter((result) => isEmailMatch(email, result.email))
            .map((result) => fromSearchResult(result, handles, "email")),
          ...(byPhone ?? [])
            .filter((result) => isPhoneMatch(phone, result.phone))
            .map((result) => fromSearchResult(result, handles, "phone"))
        ]

        setMatches(rankCustomerMatches(candidates))
      } catch {
        if (seq !== latest.current) return
        setMatches([])
      } finally {
        if (seq === latest.current) setLoading(false)
      }
    })()
  }, MATCH_DEBOUNCE_MS)

  const { first_name, last_name, email, phone } = typed

  useEffect(() => {
    lookup({ first_name, last_name, email, phone })
  }, [first_name, last_name, email, phone, lookup])

  const reset = () => {
    latest.current += 1
    setMatches([])
    setLoading(false)
  }

  return { matches, loading, reset }
}

export { type CustomerMatchInput, MATCH_DEBOUNCE_MS, useCustomerMatches }
